// frontend/src/pages/Contact.js
import React, { useState, useEffect } from 'react';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import GoogleMap from '../components/GoogleMap';
import useSiteSettings from '../hooks/useSiteSettings';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  MessageCircle,
  User,
  Building,
  CheckCircle,
  Loader
} from 'lucide-react';

const Contact = () => {
  
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await apiService.getSiteSettings();
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error fetching site settings:', error);
      setSiteSettings({
        phone: '۰۲۱-۱۲۳۴۵۶۷۸',
        mobile: '۰۹۱۲۳۴۵۶۷۸۹',
        email: 'info@businesspro.ir',
        address: 'تهران، خیابان ولیعصر، پلاک ۱۲۳۴',
        working_hours: 'شنبه تا چهارشنبه: ۸:۰۰ تا ۱۷:۰۰\nپنجشنبه: ۸:۰۰ تا ۱۳:۰۰'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // ارسال فرم به API
      await apiService.submitContact(formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('خطا در ارسال پیام. لطفاً دوباره تلاش کنید.');
    } finally {
      setSubmitting(false);
    }
  };

  // مختصات شرکت 
  const companyLocation = {
    lat: 32.7075833,
    lng: 51.5769444
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="گروه صنعتی تکنو پلیمر اسپادانا - قالب سازی و لاستیک سازی"
        description=" گروه صنعتی تکنو پلیمر اسپادانا طراحی و ساخت انواع قالب های لاستیک و تولید انواع قطعات لاستیکی از جمله اورینگ، پکینگ، قطعات صنعتی و خودرویی با انواع متریال (EPDM, HNBR, VITON, SILICON, SBR, NBR)"
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-home.jpg')}
      />

      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">📞 تماس با تکنو پلیمر اسپادانا</h1>
            <p className="text-gray-400 text-lg">ما آماده پاسخگویی به سوالات و دریافت پیشنهادات شما هستیم</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Contact Card */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6">اطلاعات تماس</h2>
                
                <div className="space-y-4">
                  {/* Phone */}
                  {siteSettings?.phone && (
                    <div className="flex items-start space-x-3 space-x-reverse">
                      <div className="bg-yellow-400 p-3 rounded-lg">
                        <Phone className="text-gray-900" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">تلفن</h3>
                        <a 
                          href={`tel:${siteSettings.phone}`}
                          className="text-gray-300 hover:text-yellow-400 transition-colors"
                        >
                          {siteSettings.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {siteSettings?.email && (
                    <div className="flex items-start space-x-3 space-x-reverse">
                      <div className="bg-yellow-400 p-3 rounded-lg">
                        <Mail className="text-gray-900" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">ایمیل</h3>
                        <a 
                          href={`mailto:${siteSettings.email}`}
                          className="text-gray-300 hover:text-yellow-400 transition-colors"
                        >
                          {siteSettings.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Address */}
                  {siteSettings?.address && (
                    <div className="flex items-start space-x-3 space-x-reverse">
                      <div className="bg-yellow-400 p-3 rounded-lg">
                        <MapPin className="text-gray-900" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">آدرس</h3>
                        <p className="text-gray-300 leading-relaxed">
                          {siteSettings.address}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Working Hours */}
                  {siteSettings?.working_hours && (
                    <div className="flex items-start space-x-3 space-x-reverse">
                      <div className="bg-yellow-400 p-3 rounded-lg">
                        <Clock className="text-gray-900" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">ساعات کاری</h3>
                        <p className="text-gray-300">
                          {siteSettings.working_hours}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h3 className="text-xl font-bold text-white mb-4">چرا تکنو پلیمر اسپادانا؟</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                    <CheckCircle size={16} className="text-green-400" />
                    <span>بیش از 10 سال تجربه در صنعت</span>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                    <CheckCircle size={16} className="text-green-400" />
                    <span>پشتیبانی ۲۴/۷</span>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                    <CheckCircle size={16} className="text-green-400" />
                    <span>گارانتی معتبر</span>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse text-gray-300">
                    <CheckCircle size={16} className="text-green-400" />
                    <span>خدمات پس از فروش</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6">ارسال پیام</h2>

                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">پیام شما با موفقیت ارسال شد!</h3>
                    <p className="text-gray-400 mb-6">
                      همکاران ما در اسرع وقت با شما تماس خواهند گرفت.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
                    >
                      ارسال پیام جدید
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                          نام و نام خانوادگی *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                            placeholder="نام خود را وارد کنید"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                          ایمیل *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                            placeholder="email@example.com"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                          شماره تماس
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                            placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                          />
                        </div>
                      </div>

                      {/* Company */}
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                          نام شرکت
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                            placeholder="نام شرکت (اختیاری)"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                        موضوع *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      >
                        <option value="">انتخاب موضوع</option>
                        <option value="استعلام قیمت">استعلام قیمت</option>
                        <option value="پشتیبانی فنی">پشتیبانی فنی</option>
                        <option value="همکاری">درخواست همکاری</option>
                        <option value="انتقادات و پیشنهادات">انتقادات و پیشنهادات</option>
                        <option value="سایر">سایر</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        متن پیام *
                      </label>
                      <div className="relative">
                        <MessageCircle className="absolute left-3 top-3 text-gray-400" size={20} />
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows="6"
                          value={formData.message}
                          onChange={handleInputChange}
                          className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all resize-none"
                          placeholder="متن پیام خود را اینجا بنویسید..."
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-yellow-400 text-gray-900 py-4 px-6 rounded-lg font-bold hover:bg-yellow-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 space-x-reverse"
                    >
                      {submitting ? (
                        <Loader className="animate-spin h-5 w-5" />
                      ) : (
                        <Send size={20} />
                      )}
                      <span>{submitting ? 'در حال ارسال...' : 'ارسال پیام'}</span>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Google Map Section */}
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-6">📍 موقعیت ما روی نقشه</h2>
            {/* <GoogleMap
              center={companyLocation}
              zoom={15}
              height="400px"
              markerTitle="BusinessPro"
              address={siteSettings?.address || "تهران، خیابان ولیعصر"}
              className="w-full"
            /> */}
            
            <div className="relative h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3357.1900678487505!2d51.574363675659846!3d32.707576773692665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzLCsDQyJzI3LjMiTiA1McKwMzQnMzcuMCJF!5e0!3m2!1sen!2s!4v1763482526285!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BusinessPro Location"
              ></iframe>
            </div>
            {/* Map Instructions */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-400">
              <div className="flex items-center space-x-2 space-x-reverse">
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <span>موقعیت شرکت روی نقشه</span>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                <span>دسترسی به مترو</span>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span>پارکینگ اختصاصی</span>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <span><a 
                      href="https://maps.app.goo.gl/9APcNubRPR8yhHTn9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-yellow-400 hover:text-yellow-300"
                    >
                      مشاهده در Google Maps
                    </a></span>
              </div>
            </div>
          </div>

          {/* Additional Contact Methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center">
              <Phone className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">تماس تلفنی</h3>
              <p className="text-gray-400 text-sm mb-4">
                031-91550821<br />
                برای دریافت مشاوره فوری
              </p>
              {siteSettings?.phone && (
                <a 
                  href={`tel:${siteSettings.phone}`}
                  className="text-yellow-400 hover:text-yellow-300 font-medium"
                >
                  {siteSettings.phone}
                </a>
              )}
            </div>

            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center">
              <Mail className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">ایمیل</h3>
              <p className="text-gray-400 text-sm mb-4">
                <a href='mailto:info@techpolymer.ir'><span>info@techpolymer.ir</span></a>
                <br />
                برای پیام‌های رسمی و مستندات
              </p>
              {siteSettings?.email && (
                <a 
                  href={`mailto:${siteSettings.email}`}
                  className="text-yellow-400 hover:text-yellow-300 font-medium"
                >
                  {siteSettings.email}
                </a>
              )}
            </div>

            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center">
              <MapPin className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">دیدار حضوری</h3>
              <p className="text-gray-400 text-sm mb-4">
                پس از هماهنگی قبلی
              </p>
              <span className="text-yellow-400 font-medium">هماهنگی لازم</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;